import { TYPE_ID_AUCTION, PLAYER_POOL_ID_ALL, PLAYER_POOL_ID_ROOKIES_ONLY, PLAYER_POOL_ID_VETS_ONLY } from './DraftHelper.js';
import PositionHelper from './PositionHelper.js';
import RosterSpotHelper from './RosterSpotHelper.js';

/**
 * @type {number} League type string constant for redraft leagues
 */
export const LEAGUE_TYPE_REDRAFT = 'redraft';

/**
 * @type {number} League type string constant for keeper leagues
 */
export const LEAGUE_TYPE_KEEPER = 'keeper';

/**
 * @type {number} League type string constant for dynasty leagues
 */
export const LEAGUE_TYPE_DYNASTY = 'dynasty';

/**
 * @type {number} League type string constant for best ball leagues
 */
export const LEAGUE_TYPE_BEST_BALL = 'bestball';

/**
 * @type {array} League options with human readable label
 */
export const LEAGUE_TYPE_OPTIONS = [
    { value: LEAGUE_TYPE_BEST_BALL, label: 'Best Ball' },
    { value: LEAGUE_TYPE_DYNASTY, label: 'Dynasty' },
    { value: LEAGUE_TYPE_KEEPER, label: 'Keeper' },
    { value: LEAGUE_TYPE_REDRAFT, label: 'Redraft' },
];

/**
 * @type {number} Value for `preferred_fa_suggestion_type_id` for suggestions
 * based on `dsValue` or "Overall Impact"
 */
export const PREFERRED_FA_SUGGESTION_TYPE_ID_DS_VALUE = 1;

/**
 * @type {number} Value for `preferred_fa_suggestion_type_id` for suggestions
 * based on `weekly3dPts` or "This Week's Boost"
 */
export const PREFERRED_FA_SUGGESTION_TYPE_ID_THIS_WEEK = 2;

/**
 * @type {number} Value for `preferred_fa_suggestion_type_id` for suggestions
 * based on `twoWeekPts` or "Next Week's Streamers"
 */
export const PREFERRED_FA_SUGGESTION_TYPE_ID_NEXT_WEEK = 3;

/**
 * @type {number} Value for `preferred_fa_suggestion_type_id` for suggestions
 * based on `dsValueAlt` or "High Ceiling Adds"
 */
export const PREFERRED_FA_SUGGESTION_TYPE_ID_DS_VALUE_ALT = 4;

/**
 * @var array Maps each starters field to a roster spot. The roster s pots
 * are used in draft board to keep track of who has been drafted
 */
export const ROSTER_SPOTS_TO_STARTERS_FIELDS = {
    [PositionHelper.QB]: 'starting_qbs',
    [PositionHelper.TQB]: 'starting_tqbs',
    [PositionHelper.RB]: 'starting_rbs',
    [PositionHelper.WR]: 'starting_wrs',
    [PositionHelper.TE]: 'starting_tes',
    [PositionHelper.DEF]: 'starting_defs',
    [PositionHelper.K]: 'starting_ks',
    [PositionHelper.TK]: 'starting_tks',
    [PositionHelper.DL]: 'starting_dls',
    [PositionHelper.LB]: 'starting_lbs',
    [PositionHelper.DB]: 'starting_dbs',
    [PositionHelper.HC]: 'starting_hcs',
    [RosterSpotHelper.QB_RB_WR_TE]: 'starting_flex_qb_rb_wr_te',
    [RosterSpotHelper.RB_WR_TE]: 'starting_flex_rb_wr_te',
    [RosterSpotHelper.RB_WR]: 'starting_flex_rb_wr',
    [RosterSpotHelper.WR_TE]: 'starting_flex_wr_te',
    [RosterSpotHelper.DL_LB_DB]: 'starting_idp_flex',
};

/**
 * Returns the fantasy positions that should be included based on a roster
 * spot in a league. For example, for FLEX this will determine what fantasy
 * positions can fill that roster spot.
 * @param {object} mvpBoardScoring The `mvp_board_scoring` record
 * @param {string} rosterSpot
 * @return array
 */
export const getFantasyPositionsForRosterSpot = function (rosterSpot, mvpBoardScoring) {
    switch (rosterSpot) {
        case PositionHelper.ALL:
            return [PositionHelper.ALL];
        case PositionHelper.QB:
            return [PositionHelper.QB];
        case PositionHelper.TQB:
            return [PositionHelper.TQB];
        case PositionHelper.RB:
            return [PositionHelper.RB];
        case PositionHelper.WR:
            return [PositionHelper.WR];
        case PositionHelper.TE:
            return [PositionHelper.TE];
        case PositionHelper.IDP:
            return [PositionHelper.DL, PositionHelper.DB, PositionHelper.LB];
        case PositionHelper.K:
            return [PositionHelper.K];
        case PositionHelper.TK:
            return [PositionHelper.TK];
        case PositionHelper.DEF:
            return [PositionHelper.DEF];
        case PositionHelper.DL:
            return [PositionHelper.DL];
        case PositionHelper.DB:
            return [PositionHelper.DB];
        case PositionHelper.LB:
            return [PositionHelper.LB];
        case PositionHelper.HC:
            return [PositionHelper.HC];
        case RosterSpotHelper.QB_RB_WR_TE:
            const fantasyPositions = [];
            if (mvpBoardScoring?.starting_qbs || !mvpBoardScoring?.starting_tqbs) {
                fantasyPositions.push(PositionHelper.QB);
            } else {
                fantasyPositions.push(PositionHelper.TQB);
            }
            fantasyPositions.push(PositionHelper.RB);
            fantasyPositions.push(PositionHelper.WR);
            fantasyPositions.push(PositionHelper.TE);
            return fantasyPositions;
        case RosterSpotHelper.RB_WR_TE:
            return [PositionHelper.RB, PositionHelper.WR, PositionHelper.TE];
        case RosterSpotHelper.RB_WR:
            return [PositionHelper.RB, PositionHelper.WR];
        case RosterSpotHelper.WR_TE:
            return [PositionHelper.WR, PositionHelper.TE];
        case RosterSpotHelper.DL_LB_DB:
            return [PositionHelper.DL, PositionHelper.DB, PositionHelper.LB];
        default:
            throw new Error('Could not determine fantasy positions for roster spot ' + rosterSpot);
    }
};

/**
 * Gets the fantasy positions on the team by checking which roster spots have
 * starters. Accounts for fantasy positions in FLEX roster spots even when those
 * positions do not have unique starting roster spots.
 * @param {Object} mvpBoardScoring Represents the `mvp_board_scoring` record
 * @returns {Array}
 */
export const getFantasyPositionsInLeague = function (mvpBoardScoring) {
    let fantasyPositionsInLeague = [];
    for (let rosterSpotAbbr in ROSTER_SPOTS_TO_STARTERS_FIELDS) {
        const numStarters = mvpBoardScoring[ROSTER_SPOTS_TO_STARTERS_FIELDS[rosterSpotAbbr]];
        if (numStarters > 0) {
            fantasyPositionsInLeague = fantasyPositionsInLeague.concat(getFantasyPositionsForRosterSpot(rosterSpotAbbr, mvpBoardScoring));
        }
    }
    return [...new Set(fantasyPositionsInLeague)]; // --- Eliminates duplicates
};

/**
 * Returns an array of the roster positions in the league based on setup
 * @param {object} mvpBoardScoring
 * @param {bool} qbFlexAsQb whether to return a QB|RB|WR|TE roster spot as
 * a QB. This is mostly done on the draft board or in value calculations.
 * @return array
 */
export const getRosterSpotsInLeague = function (mvpBoardScoring, qbFlexAsQb = false) {
    const rosterSpotsInLeague = [];
    for (let rosterSpotAbbr in ROSTER_SPOTS_TO_STARTERS_FIELDS) {
        let numStarters = 0;
        if (qbFlexAsQb) {
            numStarters = getDsNumStartersForRosterSpot(rosterSpotAbbr, mvpBoardScoring);
        } else {
            numStarters = mvpBoardScoring[ROSTER_SPOTS_TO_STARTERS_FIELDS[rosterSpotAbbr]];
        }
        if (numStarters > 0) {
            rosterSpotsInLeague.push(rosterSpotAbbr);
        }
    }
    return rosterSpotsInLeague;
};

/**
 * Returns the roster spot for a fantasy position taking into account
 * whether there are flex spots for it
 * @todo see if we need to return an array since now technically a
 * fantasy position can have multiple flex roster spots
 * @param {string} fantasyPosition
 * @param {object} mvpBoardScoring
 * @param {bool} qbFlexAsQb whether to return a QB|RB|WR|TE roster spot as
 * a QB. This is mostly done on the draft board or in value calculations.
 * @returns string
 */
export const getRosterSpotsForFantasyPosition = function (fantasyPosition, mvpBoardScoring, qbFlexAsQb = false) {
    const rosterSpotsForFantasyPosition = [];
    const rosterSpotsInLeague = getRosterSpotsInLeague(mvpBoardScoring, qbFlexAsQb);
    for (let i = 0; i < rosterSpotsInLeague.length; i++) {
        const fantasyPositionsForRosterSpot = getFantasyPositionsForRosterSpot(rosterSpotsInLeague[i], mvpBoardScoring);
        if (fantasyPositionsForRosterSpot.includes(fantasyPosition)) {
            rosterSpotsForFantasyPosition.push(rosterSpotsInLeague[i]);
        }
    }
    return rosterSpotsForFantasyPosition;
};

/**
 * Returns the number of starters for a roster spot, taking into consideration
 * that DS always consider a flex spot with a starting qb as a starting qb
 * @param rosterSpot
 * @param mvpBoardScoring
 */
export const getDsNumStartersForRosterSpot = function (rosterSpot, mvpBoardScoring) {
    if (rosterSpot == RosterSpotHelper.QB_RB_WR_TE) {
        return 0;
    }
    let numStarters = mvpBoardScoring[ROSTER_SPOTS_TO_STARTERS_FIELDS[rosterSpot]];
    if (rosterSpot == PositionHelper.QB) {
        numStarters += mvpBoardScoring.starting_flex_qb_rb_wr_te;
    }
    return numStarters;
};

/**
 * @param {object} Represents an `mvp_board_scoring` record
 * @return array
 */
const getFantasyPositionsEligibleForFlexRosterSpots = function (mvpBoardScoring) {
    if (mvpBoardScoring.starting_flex_qb_rb_wr_te > 0) {
        return [PositionHelper.QB, PositionHelper.RB, PositionHelper.WR, PositionHelper.TE];
    }

    if (mvpBoardScoring.starting_flex_rb_wr_te > 0) {
        return [PositionHelper.RB, PositionHelper.WR, PositionHelper.TE];
    }

    const flexPositions = [];
    if (mvpBoardScoring.starting_flex_rb_wr > 0) {
        flexPositions.push(PositionHelper.RB);
        flexPositions.push(PositionHelper.WR);
    }
    if (mvpBoardScoring.starting_flex_wr_te > 0) {
        flexPositions.push(PositionHelper.WR);
        flexPositions.push(PositionHelper.TE);
    }
    return [...new Set(flexPositions)];
};

/**
 * @param {object} mvpBoardScoring
 * @param {string} fantasyPosition
 * @return boolean Whether or not the fantasy position is considered flex
 *    based on the league scoring
 */
const fantasyPositionIsFlex = function (mvpBoardScoring, fantasyPosition) {
    return getFantasyPositionsEligibleForFlexRosterSpots(mvpBoardScoring).includes(fantasyPosition);
};

/**
 * Returns the number of starting flex spots that a fantasy position
 * could potentailly fill based on league scoring setup
 * @param {object} mvpBoardScoring
 * @param {string} fantasyPosition
 * @return number
 */
export const getNumAtFlexForFantasyPosition = function (mvpBoardScoring, fantasyPosition) {
    if (fantasyPosition == PositionHelper.QB) {
        return mvpBoardScoring.starting_flex_qb_rb_wr_te;
    }
    if (fantasyPosition == PositionHelper.RB) {
        return mvpBoardScoring.starting_flex_qb_rb_wr_te + mvpBoardScoring.starting_flex_rb_wr_te + mvpBoardScoring.starting_flex_rb_wr;
    }
    if (fantasyPosition == PositionHelper.WR) {
        return (
            mvpBoardScoring.starting_flex_qb_rb_wr_te +
            mvpBoardScoring.starting_flex_rb_wr_te +
            mvpBoardScoring.starting_flex_rb_wr +
            mvpBoardScoring.starting_flex_wr_te
        );
    }
    if (fantasyPosition == PositionHelper.TE) {
        return mvpBoardScoring.starting_flex_qb_rb_wr_te + mvpBoardScoring.starting_flex_rb_wr_te + mvpBoardScoring.starting_flex_wr_te;
    }
    if (PositionHelper.getIdpFantasyPositions().includes(fantasyPosition)) {
        return mvpBoardScoring.starting_idp_flex;
    }
    return 0;
};

/**
 * Whether or not the only RB, WR and TE players on your team are flex players
 * @returns {boolean}
 */
export const onlyRbTeWrAreFlex = function (mvpBoardScoring) {
    return (
        mvpBoardScoring.starting_flex > 0 && mvpBoardScoring.starting_wrs + mvpBoardScoring.starting_tes + mvpBoardScoring.starting_rbs == 0
    );
};

/**
 * Returns true if the given roster spot includes a fantasy position that does
 * not have a default roster spot on the team. For example, if the RB/WR/TE spot
 * was on the team but there was no starting roster spot for TE. This may be
 * used in UIs so that we properly put players into the flex spot if their fantasy
 * position's default roster spot doesn't have any starters
 * @param {object} mvpBoardScoring Represents an `mvp_board_scoring` record
 * @param {string} rosterSpot
 * @return bool
 */
const flexSpotHasFantasyPositionWithoutDefaultRosterSpot = function (mvpBoardScoring, rosterSpot) {
    switch (rosterSpot) {
        case RosterSpotHelper.QB_RB_WR_TE:
            return (
                !mvpBoardScoring.starting_qbs ||
                !mvpBoardScoring.starting_rbs ||
                !mvpBoardScoring.starting_wrs ||
                !mvpBoardScoring.starting_tes
            );
            break;
        case RosterSpotHelper.RB_WR_TE:
            return !mvpBoardScoring.starting_rbs || !mvpBoardScoring.starting_wrs || !mvpBoardScoring.starting_tes;
            break;
        case RosterSpotHelper.RB_WR:
            return !mvpBoardScoring.starting_rbs || !mvpBoardScoring.starting_wrs;
            break;
        case RosterSpotHelper.WR_TE:
            return !mvpBoardScoring.starting_wrs || !mvpBoardScoring.starting_tes;
            break;
        case RosterSpotHelper.DL_LB_DB:
            return !mvpBoardScoring.starting_dls || !mvpBoardScoring.starting_lbs || !mvpBoardScoring.starting_dbs;
            break;
    }
    return false;
};

/**
 * @return boolean whether or not the only defensive players on your team are idp flex players
 */
const onlyIdpAreFlex = function (mvpBoardScoring) {
    return (
        mvpBoardScoring.starting_idp_flex > 0 &&
        mvpBoardScoring.starting_dls + mvpBoardScoring.starting_lbs + mvpBoardScoring.starting_dbs == 0
    );
};

/**
 * Whether or not a league is a keeper setup
 * @param object mvpBoardScoring
 * @return boolean
 */
export const leagueIsKeeper = function (mvpBoardScoring) {
    return mvpBoardScoring.league_type == LEAGUE_TYPE_KEEPER;
};

/**
 * Whether or not a league is keeper setup w/only a couple of keepers
 * @param object mvpBoardScoring
 * @return boolean
 */
const leagueIsKeeperSmall = function (mvpBoardScoring) {
    return leagueIsKeeper(mvpBoardScoring) && mvpBoardScoring.num_keepers <= 2;
};

/**
 * Whether or not a league is keeper setup w/a medium amount of keepers
 * @param object mvpBoardScoring
 * @return boolean
 */
const leagueIsKeeperMedium = function (mvpBoardScoring) {
    return leagueIsKeeper(mvpBoardScoring) && mvpBoardScoring.num_keepers > 2 && mvpBoardScoring.num_keepers <= 8;
};

/**
 * Whether or not a league is keeper setup w/a large amount of keepers
 * @param object mvpBoardScoring
 * @return boolean
 */
const leagueIsKeeperLarge = function (mvpBoardScoring) {
    return leagueIsKeeper(mvpBoardScoring) && mvpBoardScoring.num_keepers > 8;
};

/**
 * Whether or not a league is a dynasty setup
 * @param object mvpBoardScoring
 * @return boolean
 */
export const leagueIsDynasty = function (mvpBoardScoring) {
    return mvpBoardScoring.league_type == LEAGUE_TYPE_DYNASTY;
};

/**
 * Whether or not a league is a beset ball setup
 * @param object mvpBoardScoring
 * @return boolean
 */
export const leagueIsBestBall = function (mvpBoardScoring) {
    return mvpBoardScoring.league_type == LEAGUE_TYPE_BEST_BALL;
};

/**
 * Whether or not the league has a superflex setup
 */
export const leagueIsSuperflex = function (mvpBoardScoring) {
    return mvpBoardScoring.starting_qbs + mvpBoardScoring.starting_tqbs + mvpBoardScoring.starting_flex_qb_rb_wr_te > 1;
};

/**
 * Whether or not the league has a a TE Premium setup
 */
const leagueIsTePremium = function (mvpBoardScoring) {
    return mvpBoardScoring.rec_catch_te_pts > mvpBoardScoring.rec_catch_pts;
};

/**
 * Whether or not a league is a redraft setup
 * @param object mvpBoardScoring
 * @return boolean
 */
export const leagueIsRedraft = function (mvpBoardScoring) {
    return mvpBoardScoring.league_type == LEAGUE_TYPE_REDRAFT;
};

/**
 * Whether or not a league is a dynasty league
 * @param {object} mvpBoardScoring
 * @return boolean
 */
export const leagueAppliesKeeperDynastyData = function (mvpBoardScoring) {
    return leagueIsKeeperMedium(mvpBoardScoring) || leagueIsKeeperLarge(mvpBoardScoring) || leagueIsDynasty(mvpBoardScoring);
};

/**
 * Whether or not a league has draft pick trading functionality
 * @param {object} mvpBoardScoring Represents an `mvp_board_scoring` record
 * @return boolean
 */
export const leagueCanTradePicks = function (mvpBoardScoring) {
    return leagueIsKeeper(mvpBoardScoring) || leagueIsDynasty(mvpBoardScoring);
};

/**
 * Whether or not a league is doing an auction draft
 * @param object mvpBoardScoring
 * @return boolean
 */
export const draftTypeIsAuction = mvpBoardScoring => {
    return mvpBoardScoring.draft_type_id == TYPE_ID_AUCTION;
};

/**
 * Returns true if the draft player pool is rookies only.
 * @param {object} mvpBoardScoring Represents an `mvp_board_scoring` record
 * @returns {boolean}
 */
export const draftPlayerPoolIsRookiesOnly = mvpBoardScoring => {
    return mvpBoardScoring.draft_player_pool_id == PLAYER_POOL_ID_ROOKIES_ONLY;
};

/**
 * Returns true if the draft player pool is veterans only.
 * @param {object} mvpBoardScoring Represents an `mvp_board_scoring` record
 * @returns {bool}
 */
export const draftPlayerPoolIsVetsOnly = mvpBoardScoring => {
    return mvpBoardScoring.draft_player_pool_id == PLAYER_POOL_ID_VETS_ONLY;
};

/**
 * Gets the number of players on a full team based on league setup
 * @param object mvpBoardScoring
 * @return int
 */
export const getNumForFullTeam = function (mvpBoardScoring) {
    let numForFullTeam = 0;
    for (let rosterSpotAbbr in ROSTER_SPOTS_TO_STARTERS_FIELDS) {
        numForFullTeam += mvpBoardScoring[ROSTER_SPOTS_TO_STARTERS_FIELDS[rosterSpotAbbr]];
    }
    return numForFullTeam + mvpBoardScoring.bench_spots;
};

/**
 * The number of rounds in a draft for this league. Will use the assigned
 * value on `num_rounds` if present, and if null will return value based
 * on the # of starters.
 * If we have a dynasty league, we should figure out here if we need to
 * use rookie rounds or not.
 * @param {object}
 * @return int
 */
export const getNumRounds = function (mvpBoardScoring, futureSeason = false) {
    if (leagueIsDynasty(mvpBoardScoring)) {
        if (futureSeason) {
            return mvpBoardScoring.num_rookie_rounds || mvpBoardScoring.num_rounds || getNumForFullTeam(mvpBoardScoring);
        }
        return draftPlayerPoolIsRookiesOnly(mvpBoardScoring)
            ? mvpBoardScoring.num_rookie_rounds
            : mvpBoardScoring.num_rounds || getNumForFullTeam(mvpBoardScoring);
    }

    return mvpBoardScoring.num_rounds || getNumForFullTeam(mvpBoardScoring);
};

/**
 * Returns the number of picks for a full draft
 * @param {object} mvpBoardScoring Represents the `mvp_board_scoring` record
 * @returns {number}
 */
export const getNumForFullDraft = (mvpBoardScoring, futureSeason = false) => {
    return getNumRounds(mvpBoardScoring, futureSeason) * mvpBoardScoring.num_teams;
};

/**
 * Returns the number of players for a league of full rosters
 * @param {object} mvpBoardScoring Represents the `mvp_board_scoring` record
 * @returns {number}
 */
export const getNumForFullLeague = mvpBoardScoring => {
    return getNumForFullTeam(mvpBoardScoring) * mvpBoardScoring.num_teams;
};

/**
 * Whether the league uses positional limits
 * @param mvpBoardScoring
 */
export const usesPositionalLimits = function (mvpBoardScoring) {
    return (
        mvpBoardScoring.max_qb ||
        mvpBoardScoring.max_rb ||
        mvpBoardScoring.max_wr ||
        mvpBoardScoring.max_te ||
        mvpBoardScoring.max_k ||
        mvpBoardScoring.max_def ||
        mvpBoardScoring.max_dl ||
        mvpBoardScoring.max_lb ||
        mvpBoardScoring.max_db
    );
};

/**
 * Returns true if an `mvp_board_scoring` record has data in the fields indicating
 * a league has been linked to a commissioning service
 * @param {object} mvpBoardScoring Represents the `mvp_board_scoring` record
 * for the league
 * @returns {boolean}
 */
export const leagueIsLinked = mvpBoardScoring => {
    return !!mvpBoardScoring && mvpBoardScoring.sync_source_id !== '' && mvpBoardScoring.sync_league_key !== '';
};

/**
 * Default values to use for the league scoring. Right now this is just so
 * we can make sure actual league setups have no null values when being passed
 * into the assemblyscript stuff since that has some challenges with nullable
 * values. This probably could go someplace better and should be cleaned up
 * before launch of that functionality
 */
export const DEFAULT_VALUES = {
    id: 0,
    user_id: 0,
    league_name: '',
    team_name: '',
    league_type: LEAGUE_TYPE_REDRAFT,
    num_teams: 0,
    num_rounds: 0,
    num_rookie_rounds: 0,
    auction_dollars_per_team: 0,
    draft_type_id: 0,
    draft_player_pool_id: PLAYER_POOL_ID_ALL,
    draft_position: 0,
    starting_qbs: 0,
    starting_tqbs: 0,
    starting_rbs: 0,
    starting_wrs: 0,
    starting_tes: 0,
    starting_flex_qb_rb_wr_te: 0,
    starting_flex_rb_wr_te: 0,
    starting_flex_rb_wr: 0,
    starting_flex_wr_te: 0,
    starting_ks: 0,
    starting_tks: 0,
    starting_defs: 0,
    starting_dls: 0,
    starting_lbs: 0,
    starting_dbs: 0,
    starting_idp_flex: 0,
    starting_hcs: 0,
    max_qb: 0,
    max_rb: 0,
    max_wr: 0,
    max_te: 0,
    max_k: 0,
    max_def: 0,
    max_dl: 0,
    max_lb: 0,
    max_db: 0,
    bench_spots: 0,
    pass_yds_per_pt: 0,
    pass_td_pts: 0,
    pass_td_bonus_40: 0,
    pass_td_bonus_50: 0,
    pass_cmp_pts: 0,
    pass_int_pts_lost: 0,
    pass_first_down_pts: 0,
    pass_250_pts: 0,
    pass_300_pts: 0,
    pass_350_pts: 0,
    pass_400_pts: 0,
    pass_40_pts: 0,
    rush_carry_pts: 0,
    rush_yds_per_pt: 0,
    rush_td_pts: 0,
    rush_td_bonus_40: 0,
    rush_td_bonus_50: 0,
    rush_fum_pts_lost: 0,
    rush_first_down_pts: 0,
    rush_20_pts: 0,
    rush_40_pts: 0,
    rush_100_pts: 0,
    rush_125_pts: 0,
    rush_150_pts: 0,
    rush_200_pts: 0,
    rec_catch_pts: 0,
    rec_catch_te_pts: 0,
    rec_yds_per_pt: 0,
    rec_td_pts: 0,
    rec_td_bonus_40: 0,
    rec_td_bonus_50: 0,
    rec_first_down_pts: 0,
    rec_20_pts: 0,
    rec_40_pts: 0,
    rec_100_pts: 0,
    rec_125_pts: 0,
    rec_150_pts: 0,
    rec_200_pts: 0,
    rec_target_pts: 0,
    k_xp_pts: 0,
    k_xp_miss_pts: 0,
    k_fg_miss_pts: 0,
    k_fg_0_29_pts: 0,
    k_fg_30_39_pts: 0,
    k_fg_40_49_pts: 0,
    k_fg_50_59_pts: 0,
    k_fg_60_pts: 0,
    def_td_pts: 0,
    def_special_td_pts: 0,
    def_sack_pts: 0,
    def_safety_pts: 0,
    def_int_pts: 0,
    def_fum_pts: 0,
    def_pts_allowed_0: 0,
    def_pts_allowed_1_6: 0,
    def_pts_allowed_7_13: 0,
    def_pts_allowed_14_20: 0,
    def_pts_allowed_21_27: 0,
    def_pts_allowed_28_34: 0,
    def_pts_allowed_35: 0,
    idp_tackle_pts: 0,
    idp_assist_pts: 0,
    idp_sack_pts: 0,
    idp_pass_def_pts: 0,
    idp_int_pts: 0,
    idp_fum_force_pts: 0,
    idp_fum_rec_pts: 0,
    idp_td_pts: 0,
    idp_qb_hits_pts: 0,
    idp_tackle_loss_pts: 0,
    punt_return_yds_per_pt: 0,
    kick_return_yds_per_pt: 0,
    win_pts: 0,
    pts_scored_pts: 0,
    default_system_id: 0,
    sync_source_id: '',
    sync_league_key: '',
    sync_league_extras: '',
    sync_league_auth: '',
    sync_last_picks_update: '',
    sync_enabled: false,
    sync_can_draft_players: false,
    mock_draft: false,
    preferred_fa_suggestion_type_id: 0,
    sort_order: 0,
    draft_time: '',
    draft_complete_time: '',
    create_time: '',
    update_time: '',

    /**
     * @type {number} Holds the timestamp for the last time this league was updated
     * in the browser IndexedDb after getting data from the server. This IS NOT a
     * column in the `mvp_board_scoring` table but it makes the most sense to put
     * this here and have it stored in the IndexedDb
     */
    lastUpdatedFromServerTimestamp: 0,
};

/**
 * Checks whether the draft for this year has started. If the league has no
 * draft time set or it is for last year this will return null;
 * @param {object} mvpBoardScoring Represents the `mvp_board_scoring` record
 * @returns {bool|null} Returns boolean true or false if it can properly be
 * determined and null if it cannot
 */
export const thisYearsDraftHasStarted = mvpBoardScoring => {
    if (!mvpBoardScoring.draft_time) {
        return null;
    }
    const draftDate = new Date(mvpBoardScoring.draft_time);
    const currentDate = new Date();
    if (draftDate.getFullYear() < currentDate.getFullYear()) {
        return null;
    }

    return currentDate.getTime() >= draftDate.getTime();
};

/**
 * Checks whether the draft for this year has ended. If the league has no
 * draft time set or it is for last year this will return null;
 *
 * @todo this doesn't yet account for leagues that have multiple drafts
 *
 * @param {object} mvpBoardScoring Represents the `mvp_board_scoring` record
 * @returns {bool|null} Returns boolean true or false if it can properly be
 * determined and null if it cannot
 */
export const thisYearsDraftHasCompleted = mvpBoardScoring => {
    if (!mvpBoardScoring.draft_complete_time) {
        return null;
    }

    const currentDate = new Date();
    const draftCompleteDate = new Date(mvpBoardScoring.draft_complete_time);
    if (draftCompleteDate.getFullYear() < currentDate.getFullYear()) {
        return null;
    }

    return currentDate.getTime() >= draftCompleteDate.getTime();
};

/**
 * @param {object} mvpBoardScoring Represents an `mvp_board_scoring` record
 * @returns {boolean} whether or not this Draft War Room has IDP
 */
export const hasIdp = mvpBoardScoring => {
    return (
        mvpBoardScoring.starting_dls + mvpBoardScoring.starting_lbs + mvpBoardScoring.starting_dbs + mvpBoardScoring.starting_idp_flex > 0
    );
};

/**
 * @param {object} mvpBoardScoring Represents an `mvp_board_scoring` record
 * @param {array} mvpBoardTeams The `mvp_board_team` records for the league.
 * @returns {boolean}
 */
export const usesTeamBudgets = (mvpBoardScoring, mvpBoardTeams) => {
    // --- Only valid for auctions
    if (mvpBoardScoring.draft_type_id !== TYPE_ID_AUCTION) {
        return false;
    }

    for (let i = 0; i < mvpBoardTeams.length; i++) {
        const mvpBoardTeam = mvpBoardTeams[i];
        if (mvpBoardScoring.auction_dollars_per_team != mvpBoardTeam.budget) {
            return true;
        }
    }
    return false;
};

/**
 * Ensures that all properties on mvp board scoring data have at least a default value
 * @param {object} mvpBoardScoring Represents an `mvp_board_scoring` record that we need
 * to ensure default values as required for the WASM code and its MvpBoardScoring type.
 * @return object
 */
export const assignMissingDefaultMvpBoardScoringValues = function (mvpBoardScoring) {
    for (let defaultKey in DEFAULT_VALUES) {
        ensureMvpBoardScoringPropertyHasValue(mvpBoardScoring, defaultKey);
    }
    return mvpBoardScoring;
};

/**
 * Ensures that a specific property on mvp board scoring data has at least a default value
 * @param {object} mvpBoardScoring Mvp board scoring data that we need to ensure
 * has all of the default values as required for the WASM code and its MvpBoardScoring type.
 */
export const ensureMvpBoardScoringPropertyHasValue = function (mvpBoardScoring, property) {
    if (!mvpBoardScoring.hasOwnProperty(property) || mvpBoardScoring[property] === null) {
        mvpBoardScoring[property] = DEFAULT_VALUES[property];
    }
};

/**
 * This class exports useful functions related to MvpBoardScoring models
 * and their related records in the database
 */
export default {
    LEAGUE_TYPE_REDRAFT,
    LEAGUE_TYPE_KEEPER,
    LEAGUE_TYPE_DYNASTY,
    LEAGUE_TYPE_BEST_BALL,
    ROSTER_SPOTS_TO_STARTERS_FIELDS,

    flexSpotHasFantasyPositionWithoutDefaultRosterSpot,
    getDsNumStartersForRosterSpot,
    getFantasyPositionsInLeague,
    getFantasyPositionsForRosterSpot,
    getFantasyPositionsEligibleForFlexRosterSpots,
    getNumAtFlexForFantasyPosition,
    getNumForFullTeam,
    getNumRounds,
    getRosterSpotsInLeague,
    getRosterSpotsForFantasyPosition,
    leagueAppliesKeeperDynastyData,
    fantasyPositionIsFlex,
    leagueIsKeeper,
    leagueIsKeeperSmall,
    leagueIsKeeperMedium,
    leagueIsKeeperLarge,
    leagueIsDynasty,
    leagueIsBestBall,
    leagueIsRedraft,
    leagueIsSuperflex,
    leagueIsTePremium,
    leagueCanTradePicks,
    onlyIdpAreFlex,
    hasIdp,
    /**
     * @param {object} mvpBoardScoring
     * @param {string} fantasyPosition
     * @return boolean
     */
    fantasyPositionIsIdpFlex: function (mvpBoardScoring, fantasyPosition) {
        switch (fantasyPosition) {
            case PositionHelper.DL:
            case PositionHelper.DB:
            case PositionHelper.LB:
                return mvpBoardScoring.starting_idp_flex >= 1;
            default:
                return false;
        }
    },
    /**
     * Default values to use for the league scoring. Right now this is just so
     * we can make sure actual league setups have no null values when being passed
     * into the assemblyscript stuff since that has some challenges with nullable
     * values. This probably could go someplace better and should be cleaned up
     * before launch of that functionality
     */
    DEFAULTS: DEFAULT_VALUES,
};
